import { RouterProvider, createBrowserRouter } from 'react-router-dom';

import { BusinessEntityGate } from '@zenbusiness/application-commons-business-entity';
import { EnvProvider } from '@zenbusiness/application-commons-env';
import PageError from '@zenbusiness/zui/PageError';
import PageLoading from '@zenbusiness/zui/PageLoading';

import Authenticated from '#client/App/Authenticated';
import PostRouting from '#client/App/PostRouting';
import { DEFAULT_SEGMENT } from '#client/build-env';
import AssistantLayout from '#client/components/AssistantLayout';
import * as ROUTES from '#client/constants/routes';
import * as Pages from '#client/pages';
import { generateTestRoutes } from '#client/utils/testRoutes';

const router = createBrowserRouter([
  {
    /**
     * Routes which do not require authentication at page load
     */
    element: <PostRouting />,
    children: [
      { path: ROUTES.login, element: <Pages.Login /> },
      { path: ROUTES.logout, element: <Pages.Logout /> },
      { path: `${ROUTES.logout}/*`, element: <Pages.Logout /> }, // Support path params like /logout/L2d1aWRlL2NoYXQ=
      { path: ROUTES.callback, element: <Pages.Callback /> },
      { path: `${ROUTES.callback}/*`, element: <Pages.Callback /> } // Support path params like /callback/L2d1aWRlL2NoYXQ=
    ]
  },
  {
    /**
     * Routes which can support authentication but do not require it
     */
    path: DEFAULT_SEGMENT,
    element: (
      <Authenticated optional>
        <PostRouting />
      </Authenticated>
    ),
    children: [{ index: true, element: <Pages.Landing /> }]
  },
  {
    /**
     * Visitor Route
     * /guide/visitor - Handles visitor requests without authentication
     */
    path: ROUTES.visitorThreadCreator,
    element: (
      <Authenticated optional>
        <PostRouting />
      </Authenticated>
    ),
    children: [{ index: true, element: <Pages.VisitorThreadCreator /> }]
  },
  {
    /**
     * Visitor Route
     * /guide/visitor/thread/:threadUuid - Handles threads without authentication
     */
    path: ROUTES.visitorThread,
    element: (
      <Authenticated optional>
        <AssistantLayout disableSidebar disableThreadHistory>
          <PostRouting />
        </AssistantLayout>
      </Authenticated>
    ),
    children: [{ index: true, element: <Pages.VisitorThreadViewer /> }]
  },
  {
    /**
     * Assistant Routes - Shared Layout Parent
     * Groups /guide/chat, /guide/thread/:uuid, and /guide/company/:businessEntityUuid
     * under a single <AssistantLayout> instance to prevent double header flash during navigation
     */
    path: '/guide',
    element: (
      <Authenticated optional>
        <AssistantLayout>
          <PostRouting />
        </AssistantLayout>
      </Authenticated>
    ),
    children: [
      {
        /**
         * Universal Chat Entry Route
         * /guide/chat - Creates new thread for any user mode (anonymous, starter, business)
         */
        path: 'chat',
        element: <Pages.UniversalThreadCreator autoCreate={false} />
      },
      {
        /**
         * Thread Continuation Route
         * /guide/thread/:threadUuid - Continues existing thread
         */
        path: 'thread/:threadUuid',
        element: <Pages.ThreadViewer />
      },
      {
        /**
         * Legacy Thread Creation Route
         * /guide/company/:businessEntityUuid - Auto-creates thread (business mode only)
         * Note: Uses stricter authentication via nested wrapper
         */
        path: 'company/:businessEntityUuid',
        element: (
          <Authenticated>
            <BusinessEntityGate loadingElement={<PageLoading />}>
              <Pages.UniversalThreadCreator autoCreate />
            </BusinessEntityGate>
          </Authenticated>
        )
      }
    ]
  },
  // Test Routes - Automatically included in development/local environments only
  // See src/config/testRoutes.js to add new test pages
  ...generateTestRoutes(),
  {
    path: '/guide/visitor/*',
    element: (
      <Authenticated optional>
        <PostRouting />
      </Authenticated>
    ),
    children: [
      // { index: true, element: <Pages.Landing /> },
      { path: 'business-cost', element: <Pages.BusinessCostVisitor /> },
      { path: 'business-idea', element: <Pages.BusinessIdeaVisitor /> },
      { path: 'business-name', element: <Pages.BusinessNameVisitor /> },
      { path: 'business-name-research', element: <Pages.BusinessNameVisitor /> },
      {
        path: 'business-entity-advisor',
        element: (
          <Pages.StandaloneVisitorPage
            formAgentName="business-entity-advisor-form"
            pageTag="business-entity-advisor"
            persistKey="business-entity-advisor-state"
            reportAgentName="business-entity-advisor"
            subtitle="Get guidance on choosing the right business structure"
            title="Business Entity Advisor"
          />
        )
      },
      {
        path: 'market-analysis',
        element: (
          <Pages.StandaloneVisitorPage
            formAgentName="market-analysis-form"
            pageTag="market-analysis"
            persistKey="market-analysis-state"
            reportAgentName="market-analysis"
            subtitle="Get AI-powered insights about your market, competitors, and growth opportunities."
            title="Market Analysis Tool"
          />
        )
      }
    ]
  },
  //   /**
  //    * Trailing Slash Redirect - redirects /guide/chat/ to /guide/chat
  //    */
  //   path: `${ROUTES.chatEntry}/`,
  //   element: <Navigate replace to={ROUTES.chatEntry} />
  // },
  {
    path: '*',
    element: <PageError>The requested route could not be found</PageError>
  }
]);

export function DirectRoutes() {
  return (
    // ROOT_SEGMENT is used to identify where the router is mounted at.
    // Since this is a direct entry router, the root is the MFE's DEFAULT_SEGMENT
    <EnvProvider env={{ ROOT_SEGMENT: DEFAULT_SEGMENT }}>
      <RouterProvider router={router} />
    </EnvProvider>
  );
}
